import React, { useState, useEffect, useRef, useCallback } from "react";
import Card from "../Card";
import "./lister.css";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { default as Axios } from "axios";
import { API_URL } from "../../config/constants";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
const CARDS_PER_PAGE = 16;
const axios = Axios.create({
  baseURL: API_URL + "/",
});

const applySort = (cards, sortBy) => {
  if (!sortBy) return [...cards];
  const result = [...cards];
  result.sort((a, b) => {
    if (sortBy === 'nombre-asc') return (a.Nombre || '').localeCompare(b.Nombre || '');
    if (sortBy === 'nombre-desc') return (b.Nombre || '').localeCompare(a.Nombre || '');
    if (sortBy === 'coste-asc') return Number(a.Coste) - Number(b.Coste);
    if (sortBy === 'coste-desc') return Number(b.Coste) - Number(a.Coste);
    if (sortBy === 'fuerza-asc') return Number(a.Fuerza) - Number(b.Fuerza);
    if (sortBy === 'fuerza-desc') return Number(b.Fuerza) - Number(a.Fuerza);
    return 0;
  });
  return result;
};

const Lister = ({ edition, sortBy }) => {
  const dispatch = useDispatch();
  const [isLoadingMoreItems, setLoadingMoreItems] = useState(false);
  const [addedCardIds, setAddedCardIds] = useState(new Set());
  const [overlayVisible, setOverlayVisible] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filteredCards, setFilteredCards] = useState([]);
  const [displayedCards, setDisplayedCards] = useState([]);
  

  const observer = useRef();
  const initialLoadDone = useRef(false);

  const lister_cards = useSelector((state) => state.lister);
  const isLoading = useSelector((state) => state.isLoading);
  const hasMoreItemsToLoad = useSelector((state) => state.hasMoreItemsToLoad);
  const nextPageToLoad = useSelector((state) => state.nextPageToLoad);
  const searchActive = useSelector((state) => state.searchActive);
  const selectedCardId = useSelector((state) => state.selectedCardId);

  const loadMoreCartas = useCallback(() => {
    const nextPage = currentPage + 1;
    const startIndex = nextPage * CARDS_PER_PAGE;
    const endIndex = startIndex + CARDS_PER_PAGE;
    const newCartas = filteredCards.slice(startIndex, endIndex);

    if (newCartas.length > 0) {
      setDisplayedCards((prev) => [...prev, ...newCartas]);
      setCurrentPage(nextPage);
    }

    setHasMore(endIndex < filteredCards.length);
  }, [currentPage, filteredCards]);
  const [isFetching] = useInfiniteScroll(loadMoreCartas, hasMore);


  // IntersectionObserver para scroll infinito
    const lastCardElementRef = useCallback(
      (node) => {
        if (isLoadingMoreItems) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreCartas();
          }
        });

        if (node) observer.current.observe(node);
      },
      [isLoadingMoreItems, hasMore, loadMoreCartas]
    );

  useEffect(() => {
    if (!searchActive && lister_cards.length === 0) return;

    const processed = applySort(lister_cards, sortBy);
    setFilteredCards(processed);
    setDisplayedCards(processed.slice(0, CARDS_PER_PAGE));
    setCurrentPage(0);
    setHasMore(processed.length > CARDS_PER_PAGE);
  }, [lister_cards, sortBy, searchActive]);


  useEffect(() => {
    if (selectedCardId) {
      setOverlayVisible(selectedCardId);
      const timer = setTimeout(() => setOverlayVisible(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [selectedCardId]);

  useEffect(() => {
    const loadCardsByEdition = async () => {
      if (searchActive) return;

      dispatch({ type: "SET_LOADING_STATE", payload: true });

      try {
        const endpoint = edition
          ? `/cartas?page=1&edition=${edition}`
          : `/cards?per_page=30`;

        const response = await axios.get(endpoint);
        const cardsData = response.data.data || [];

        dispatch({ type: "UPDATE_LISTER", payload: cardsData });
        setFilteredCards(cardsData);
        setDisplayedCards(cardsData.slice(0, CARDS_PER_PAGE));
        setCurrentPage(0);
        setHasMore(cardsData.length > CARDS_PER_PAGE);

        if (response.data.meta?.pages_remaining > 0) {
          dispatch({ type: "SET_HAS_MORE_ITEMS_TO_LOAD", payload: true });
          dispatch({ type: "SET_NEXT_PAGE_TO_LOAD", payload: response.data.meta.next_page });
        } else {
          dispatch({ type: "SET_HAS_MORE_ITEMS_TO_LOAD", payload: false });
        }
      } catch (err) {
        console.error("Error al cargar cartas por edición:", err);
      } finally {
        dispatch({ type: "SET_LOADING_STATE", payload: false });
      }
    };

    initialLoadDone.current = false;
    loadCardsByEdition();
  }, [edition, searchActive, dispatch]);

  const loadMoreItems = async () => {
    if (!nextPageToLoad) return;

    setLoadingMoreItems(true);
    try {
      const response = await axios.get(nextPageToLoad);
      const newCards = response.data.data || [];

      if (newCards.length > 0) {
        dispatch({ type: "UPDATE_LISTER_ITEMS", payload: newCards });
        setFilteredCards((prev) => [...prev, ...newCards]);
        setDisplayedCards((prev) => [...prev, ...newCards.slice(0, CARDS_PER_PAGE)]);
        setAddedCardIds(new Set(newCards.map((card) => card.id)));
        setTimeout(() => setAddedCardIds(new Set()), 1000);

        if (response.data.meta?.pages_remaining > 0) {
          dispatch({ type: "SET_NEXT_PAGE_TO_LOAD", payload: response.data.meta.next_page });
        } else {
          dispatch({ type: "SET_HAS_MORE_ITEMS_TO_LOAD", payload: false });
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error("Error al cargar más cartas:", err);
    } finally {
      setLoadingMoreItems(false);
    }
  };

  if (isLoading && lister_cards.length === 0) {
    return (
      <div className="lister" style={{ justifyContent: "center", alignItems: "center" }}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return (
    <div className="lister">
      {displayedCards.length > 0 ? (
        displayedCards.map((card, index) => {
          const cardKey = card.Id || card.id || index;
          const cardClass = `card-container-lister ${addedCardIds.has(card.id) ? "new-card" : ""}`;
          const isLastCard = index === displayedCards.length - 1;

          return (
            <div
              key={cardKey}
              className={cardClass}
              ref={isLastCard ? lastCardElementRef : null}
            >
              <Card cardInfo={card} index={card.Id} isDraggable={true} />
            </div>
          );
        })
      ) : (
        <div className="no-cards-message">No se encontraron cartas.</div>
      )}

      {isLoadingMoreItems && (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
          <CircularProgress color="secondary" />
        </div>
      )}
    </div>
  );
};

export default Lister;
